// TODO Reset validation when fixed
import React, { useEffect, useState } from "react";
import { useApi, useI18n } from "bananas-commerce-admin";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

import { PublicationSchema } from "../../types";
import RemoveButton from "../RemoveButton";
export type SelectionType = "selection" | "all";
export interface PublicationSectionProps {
  isLoadingPublications: boolean;
  selectedPublications: string[];
  onPublicationsChange: (publications: string[]) => void;
  error?: string;
}

export const PublicationSection: React.FC<PublicationSectionProps> = ({
  isLoadingPublications,
  selectedPublications,
  onPublicationsChange,
  error,
}) => {
  const { t } = useI18n();
  const api = useApi();
  const [selectionType, setSelectionType] =
    useState<SelectionType>("selection");
  const [publicationsState, setPublications] = useState<PublicationSchema[]>(
    [],
  );

  useEffect(() => {
    const fetchPublications = async () => {
      const response = await api.operations["mediahouse.context:get"]?.call();
      const data = await response?.json();
      setPublications(data.publications);
    };
    fetchPublications();
  }, []);

  return (
    <Box id="publications">
      <Typography gutterBottom variant="subtitle1">
        {t("Tidningar")}
      </Typography>
      <FormControl sx={{ width: "100%" }}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <RadioGroup
            row
            sx={{ gap: 2 }}
            value={selectionType}
            onChange={(e) => setSelectionType(e.target.value as SelectionType)}
          >
            <FormControlLabel
              control={<Radio />}
              label="Urval"
              value="selection"
            />
            <FormControlLabel
              control={<Radio />}
              label="Alla titlar"
              value="all"
              onClick={() =>
                onPublicationsChange(
                  publicationsState?.map((pub) => pub.abbreviation) ?? [],
                )
              }
            />
          </RadioGroup>
          {selectionType === "selection" && selectedPublications.length > 0 && (
            <Button
              endIcon={<ClearIcon />}
              size="small"
              sx={{ ml: "auto" }}
              onClick={() => onPublicationsChange([])}
            >
              {t("Rensa urval")}
            </Button>
          )}
        </Box>
      </FormControl>

      <Autocomplete
        clearOnBlur
        disableCloseOnSelect
        multiple
        disabled={selectionType === "all"}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) =>
          option.abbreviation === value.abbreviation
        }
        loading={isLoadingPublications}
        options={publicationsState}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Lägg till titel")}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingPublications ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Checkbox
                checked={selected}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                style={{ marginRight: 8 }}
              />
              {option.name}
            </li>
          );
        }}
        renderTags={() => null}
        size="small"
        sx={{
          bgcolor: "backgrond.paper",
          "& .MuiAutocomplete-clearIndicator": {
            display: "none",
          },
        }}
        value={publicationsState.filter((pub) =>
          selectedPublications.includes(pub.abbreviation),
        )}
        onChange={(_, newValue) => {
          onPublicationsChange(newValue.map((pub) => pub.abbreviation));
        }}
      />
      {error && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}
      {selectionType !== "all" && selectedPublications.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {publicationsState
            ?.filter((pub) => selectedPublications.includes(pub.abbreviation))
            .map((pub) => (
              <Box
                key={pub.abbreviation}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Typography>{pub.name}</Typography>
                <RemoveButton
                  sx={{ color: "text.secondary" }}
                  onClick={() => {
                    onPublicationsChange(
                      selectedPublications.filter(
                        (abbr) => abbr !== pub.abbreviation,
                      ),
                    );
                  }}
                />
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};
